import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolRosterCreateValues,
  buildSchoolRosterFields,
  buildSchoolRosterUpdateValues,
} from "pages/app/workspace/sections/forms/school-ops-section-forms";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";

type SchoolMatchRostersPanelProps = {
  listBackTo?: string;
  matchId: string;
  portalAccess: any;
  rosterProfileBackTo?: string;
  schoolId: string;
};

export const SchoolMatchRostersPanel: React.FC<SchoolMatchRostersPanelProps> = ({
  listBackTo = "/workspace/schedule",
  matchId,
  portalAccess,
  rosterProfileBackTo,
  schoolId,
}) => {
  const history = useHistory();

  const matchQuery = useQuery({
    queryKey: ["workspace-school-roster-match", matchId],
    queryFn: () => workspaceApi.schoolMatches.get(matchId),
    enabled: Boolean(matchId),
  });

  const rostersQuery = useQuery({
    queryKey: ["workspace-school-rosters", matchId],
    queryFn: () => workspaceApi.schoolRosters.list(matchId),
    enabled: Boolean(matchId),
  });

  const canEditSchoolRosters = hasPortalAction(portalAccess, "school_match_rosters.manage");
  const selectedMatch = matchQuery.data;

  const rosterTeams = useMemo(
    () =>
      selectedMatch
        ? [selectedMatch.home_school_team, selectedMatch.away_school_team].filter(Boolean)
        : [],
    [selectedMatch],
  );

  const rosterPlayersQuery = useQuery({
    queryKey: [
      "workspace-school-roster-player-directory",
      matchId,
      rosterTeams.map((team: any) => team.id).join("|"),
    ],
    queryFn: async () => {
      const results = await Promise.all(
        rosterTeams.map(async (team: any) => ({
          players: await workspaceApi.schoolTeamPlayers.list(team.id),
          teamId: team.id,
        })),
      );

      return Object.fromEntries(results.map((result) => [result.teamId, result.players]));
    },
    enabled: rosterTeams.length > 0,
  });

  const rosterFields = buildSchoolRosterFields({
    rosterPlayersByTeam: rosterPlayersQuery.data || {},
    rosterTeams,
  });

  return (
    <CrudSection
      buildCreateValues={buildSchoolRosterCreateValues}
      buildUpdateValues={buildSchoolRosterUpdateValues}
      canView={Boolean((rostersQuery.data || []).length)}
      canCreate={canEditSchoolRosters && Boolean(matchId)}
      canEdit={canEditSchoolRosters && Boolean(matchId)}
      columns={[
        { label: "Team", accessor: (row) => row.school_team_name || "Unknown" },
        { label: "Status", accessor: (row) => <StatusPill value={row.status} /> },
        { label: "Submitted By", accessor: (row) => row.submitted_by_name || "Unknown" },
        { label: "Submitted", accessor: (row) => formatDateTime(row.submitted_at) },
      ]}
      createLabel="Add roster"
      data={rostersQuery.data || []}
      description={
        canEditSchoolRosters
          ? "Manage match rosters for this fixture with guided team and player selection."
          : "Review match rosters for this fixture."
      }
      fields={rosterFields}
      invalidateKeys={[["workspace-school-rosters", matchId]]}
      isLoading={rostersQuery.isLoading || matchQuery.isLoading}
      onCreate={(payload) => workspaceApi.schoolRosters.create(matchId, payload)}
      onView={(row) => {
        if (!row?.id) {
          return;
        }

        history.push({
          pathname: `/workspace/school-rosters/${row.school_id || schoolId}/${
            row.school_match_id || matchId
          }/${row.id}`,
          state: {
            backTo: rosterProfileBackTo || listBackTo,
          },
        });
      }}
      onUpdate={(row, payload) => workspaceApi.schoolRosters.update(matchId, row.id, payload)}
      embedded
      title="Match Rosters"
    />
  );
};
